/**
 * `useSpacesAnalytics` — the single instrumentation entry point for Rovo Spaces
 * impact metrics.
 *
 * Wraps `useAnalytics` from `@atlassian/conversation-assistant-instrumentation`
 * so every event is fired on the `aiMate` channel with the shared AI Mate
 * context attributes and default tags applied. Each fire is guarded by the
 * `rovo_chat_spaces_experiment` experiment (the same experiment that renders
 * Spaces), so events are only emitted when the feature is actually enabled and
 * call sites don't need to repeat the experiment check.
 *
 * Value-moments that qualify for Rovo MAU (a chat attached to a space) also
 * fire an AI MAU event via `trackAIMAUEvent`, so Spaces contributes to the Rovo
 * Chat MAU north-star in addition to the standard product track events.
 */
import { useCallback, useMemo } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { RovoSpaceEntryPoint } from '@atlaskit/rovo-triggers/types';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { SPACES_ANALYTICS } from './constants';

/**
 * Controlled vocabularies for analytics dimensions. These are closed union
 * literals (not open `string`s) so only known, non-UGC values can reach the
 * event payload — see the no-PII note in the PR description.
 */
// Re-exported (canonical definition lives in `@atlaskit/rovo-triggers`) so
// consumers like `SpaceLandingPageProps` can keep importing it from here.
export type SpaceEntryPoint = RovoSpaceEntryPoint;
export type SpaceCreatedFrom = 'manual' | 'inChatRecommendation' | 'recommendedSpace';
export type SpaceConversationAddedFrom = 'createSpace' | 'chat';
export type SpaceSourceAddMethod = 'manual' | 'recommendation';

type SpaceCreatedParams = {
	spaceId?: string;
	/** Number of sources included at creation time. */
	sourceCount?: number;
	/** Where the create was triggered from. */
	createdFrom?: SpaceCreatedFrom;
	/** Present when a conversation is attached to the new space on creation. */
	hasInitialConversation?: boolean;
};

type SourceAddedParams = {
	spaceId: string;
	/**
	 * Backend-derived source classifier (e.g. 'confluence', 'jira'). Not user
	 * content — comes from `detectSourceType`/recommendation metadata, never a
	 * free-text title or URL.
	 */
	sourceType?: string;
	/** How the source was added — 'manual' (link picker) vs 'recommendation' (smart-context). */
	addMethod?: SpaceSourceAddMethod;
	/** Backend recommendation id when the source came from an accepted recommendation. */
	recommendationId?: string;
	/** Source count on the space after the add. */
	sourceCount?: number;
};

type SourceRemovedParams = {
	spaceId: string;
	/** Backend-derived source classifier (e.g. 'confluence', 'jira'). Not user content. */
	sourceType?: string;
};

type SourceRecommendationsViewedParams = {
	spaceId: string;
	/** Number of source recommendations shown (impression → CTR denominator). */
	recommendationCount: number;
};

type StarterClickedParams = {
	spaceId: string;
	/** Opaque id of the starter prompt that was clicked (which starter drove engagement). */
	starterId: string;
};

type ConversationAddedParams = {
	spaceId: string;
	/** Where the attach was triggered from. */
	addedFrom?: SpaceConversationAddedFrom;
	/** How the chat was added to the space — 'manual' (… menu) vs 'recommendation'. */
	method?: SpaceSourceAddMethod;
};

type SpaceViewedParams = {
	spaceId: string;
	sourceCount?: number;
	chatCount?: number;
	activeTab?: string;
	fullScreen?: boolean;
	/** How the user reached the space. */
	entryPoint?: SpaceEntryPoint;
};

type SpaceSearchedParams = {
	queryLength: number;
	resultCount?: number;
};

type MemoryCreatedParams = {
	spaceId: string;
};

type MemoriesViewedParams = {
	spaceId: string;
	/** Number of memories retrieved/shown. */
	count?: number;
};

export type SpacesAnalytics = {
	fireSpaceCreated: (params: SpaceCreatedParams) => void;
	fireSourceAdded: (params: SourceAddedParams) => void;
	fireSourceRemoved: (params: SourceRemovedParams) => void;
	fireSourceRecommendationsViewed: (params: SourceRecommendationsViewedParams) => void;
	fireConversationAdded: (params: ConversationAddedParams) => void;
	fireSpaceViewed: (params: SpaceViewedParams) => void;
	fireSpaceSearched: (params: SpaceSearchedParams) => void;
	fireStarterClicked: (params: StarterClickedParams) => void;
	fireMemoryCreated: (params: MemoryCreatedParams) => void;
	fireMemoriesViewed: (params: MemoriesViewedParams) => void;
};

export const useSpacesAnalytics = (): SpacesAnalytics => {
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();

	const fireSpaceCreated = useCallback(
		({ spaceId, sourceCount, createdFrom, hasInitialConversation }: SpaceCreatedParams) => {
			if (!FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'track',
				...SPACES_ANALYTICS.events.spaceCreated,
				attributes: {
					spaceId,
					sourceCount,
					createdFrom,
					hasInitialConversation,
				},
			});
		},
		[sendAnalyticsEvent],
	);

	const fireSourceAdded = useCallback(
		({ spaceId, sourceType, addMethod, recommendationId, sourceCount }: SourceAddedParams) => {
			if (!FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'track',
				...SPACES_ANALYTICS.events.sourceAdded,
				attributes: { spaceId, sourceType, addMethod, recommendationId, sourceCount },
			});
		},
		[sendAnalyticsEvent],
	);

	const fireSourceRemoved = useCallback(
		({ spaceId, sourceType }: SourceRemovedParams) => {
			if (!FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'track',
				...SPACES_ANALYTICS.events.sourceRemoved,
				attributes: { spaceId, sourceType },
			});
		},
		[sendAnalyticsEvent],
	);

	const fireSourceRecommendationsViewed = useCallback(
		({ spaceId, recommendationCount }: SourceRecommendationsViewedParams) => {
			if (!FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'ui',
				...SPACES_ANALYTICS.events.sourceRecommendationsViewed,
				attributes: { spaceId, recommendationCount },
			});
		},
		[sendAnalyticsEvent],
	);

	const fireConversationAdded = useCallback(
		({ spaceId, addedFrom, method }: ConversationAddedParams) => {
			if (!FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'track',
				...SPACES_ANALYTICS.events.conversationAdded,
				attributes: { spaceId, addedFrom, method },
			});
			// Value-moment: count this toward Rovo Chat MAU.
			trackAIMAUEvent({
				type: 'actioned',
				source: SPACES_ANALYTICS.mauSource,
				subjectId: SPACES_ANALYTICS.events.conversationAdded.actionSubject,
				actionResult: 'success',
				attributes: { spaceId, addedFrom, method },
			});
		},
		[sendAnalyticsEvent, trackAIMAUEvent],
	);

	const fireSpaceViewed = useCallback(
		({ spaceId, sourceCount, chatCount, activeTab, fullScreen, entryPoint }: SpaceViewedParams) => {
			if (!FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'screen',
				name: SPACES_ANALYTICS.screens.spaceLandingPage,
				attributes: { spaceId, sourceCount, chatCount, activeTab, fullScreen, entryPoint },
			});
		},
		[sendAnalyticsEvent],
	);

	const fireSpaceSearched = useCallback(
		({ queryLength, resultCount }: SpaceSearchedParams) => {
			if (!FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'track',
				...SPACES_ANALYTICS.events.spaceSearched,
				attributes: { queryLength, resultCount },
			});
		},
		[sendAnalyticsEvent],
	);

	const fireStarterClicked = useCallback(
		({ spaceId, starterId }: StarterClickedParams) => {
			if (!FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'ui',
				...SPACES_ANALYTICS.events.starterClicked,
				attributes: { spaceId, starterId },
			});
		},
		[sendAnalyticsEvent],
	);

	const fireMemoryCreated = useCallback(
		({ spaceId }: MemoryCreatedParams) => {
			if (!FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'track',
				...SPACES_ANALYTICS.events.memoryCreated,
				attributes: { spaceId },
			});
		},
		[sendAnalyticsEvent],
	);

	const fireMemoriesViewed = useCallback(
		({ spaceId, count }: MemoriesViewedParams) => {
			if (!FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'ui',
				...SPACES_ANALYTICS.events.memoriesViewed,
				attributes: { spaceId, count },
			});
		},
		[sendAnalyticsEvent],
	);

	return useMemo(
		() => ({
			fireSpaceCreated,
			fireSourceAdded,
			fireSourceRemoved,
			fireSourceRecommendationsViewed,
			fireConversationAdded,
			fireSpaceViewed,
			fireSpaceSearched,
			fireStarterClicked,
			fireMemoryCreated,
			fireMemoriesViewed,
		}),
		[
			fireSpaceCreated,
			fireSourceAdded,
			fireSourceRemoved,
			fireSourceRecommendationsViewed,
			fireConversationAdded,
			fireSpaceViewed,
			fireSpaceSearched,
			fireStarterClicked,
			fireMemoryCreated,
			fireMemoriesViewed,
		],
	);
};
